import React from 'react';
import { Link } from 'react-router-dom';
import { Widget } from '../Widget';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { useGovernance } from '../../../contexts/GovernanceContext';
import type { HealthState } from '../../../types/governance';
const STATE: Record<HealthState, {
  label: string;
  tone: 'ok' | 'warn' | 'danger' | 'neutral';
}> = {
  healthy: {
    label: 'Operational',
    tone: 'ok'
  },
  degraded: {
    label: 'Degraded',
    tone: 'warn'
  },
  down: {
    label: 'Unavailable',
    tone: 'danger'
  },
  /* Deliberately not "Operational". An unknown service is unknown. */
  unknown: {
    label: 'Not reporting',
    tone: 'neutral'
  }
};

/**
 * Reads the integration register rather than a health mock. An unconfigured
 * provider reports "Not configured" and an unreachable one "Not reporting" —
 * neither is allowed to render as Operational, because a green tick nobody
 * verified is worse than an honest gap.
 */
export function HealthWidget() {
  const {
    integrations,
    webhooks,
    jobs
  } = useGovernance();
  const dead = webhooks.filter(hook => hook.state === 'dead_lettered').length;
  const failing = jobs.filter(job => job.state === 'failed').length;
  return <Widget title="System health" description="Integrations Campuzon depends on, as each last reported." state={integrations.length ? 'ready' : 'empty'} emptyTitle="No integrations registered" emptyDescription="Nothing has been configured for this deployment yet." action={<Link to="/admin/health" data-id="element-486" data-mp="3vnyae2xltm1b">
          <Button variant="secondary" size="sm" data-id="element-487" data-mp="3vo6lpqe2wjae">
            Open system health
          </Button>
        </Link>} footnote="Status is what the provider last reported, not a live probe from this page." data-id="element-485" data-mp="4h3uiyy8qd06">
      <ul className="space-y-2" data-id="element-488" data-mp="3mo1r6gtzhfdn">
        {integrations.slice(0, 5).map(integration => {
        const state = STATE[integration.health];
        return <li key={integration.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-line bg-canvas px-3 py-2" data-id="element-489" data-mp="1kubyuz3ho4x5">
              <div className="min-w-0" data-id="element-490" data-mp="1edsm7abeybkk">
                <p className="truncate text-body-sm font-medium text-ink" data-id="element-491" data-mp="1egy9ql4e95e6">{integration.provider}</p>
                <p className="truncate text-caption text-ink-subtle" data-id="element-492" data-mp="19hbuk6509f2q">
                  {integration.purpose} · {integration.environment}
                </p>
              </div>
              {integration.configured ? <Badge tone={state.tone} data-id="element-493" data-mp="1x4jq3wcmt9rk">{state.label}</Badge> : <Badge tone="neutral" data-id="element-494" data-mp="1sed506hbw7e2">Not configured</Badge>}
            </li>;
      })}
      </ul>

      {(dead > 0 || failing > 0) && <p className="mt-3 rounded-lg bg-warn-50 px-3 py-2 text-caption leading-5 text-warn-800" data-id="element-495" data-mp="1neqp1pa48d1k">
          {dead > 0 && `${dead} webhook ${dead === 1 ? 'payload is' : 'payloads are'} dead-lettered and held for inspection. `}
          {failing > 0 && `${failing} background ${failing === 1 ? 'job has' : 'jobs have'} failed and ${failing === 1 ? 'is' : 'are'} awaiting its owner.`}
        </p>}
    </Widget>;
}